import { useMemo } from 'react';
import EventIcon from '@mui/icons-material/Event';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import {
  getSurfaceBorder,
  getSurfaceColor,
  getThemeTokens,
  radii,
} from '../../../theme/tokens';
import { EntityRefChips } from '../../common/EntityRefChips';
import { emailToUserRef, useUsersByEmail } from '../../common/useUsersByEmail';
import { useActiveAlerts, type ActiveAlert } from '../hooks/useActiveAlerts';
import { useOnCall, type OnCallSchedule } from '../hooks/useOnCall';

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
};

const formatNextOnCall = (schedule: OnCallSchedule): string | null => {
  if (!schedule.myNextOnCall) return null;
  if (schedule.myNextOnCall.start === 'now') return "You're on-call now";
  const start = formatDate(schedule.myNextOnCall.start);
  const end = formatDate(schedule.myNextOnCall.end);
  return `Your next shift: ${start} – ${end}`;
};

const priorityConfig: Record<
  string,
  { label: string; color: string; bg: string }
> = {
  P1: { label: 'P1', color: '#fff', bg: '#e74c3c' },
  P2: { label: 'P2', color: '#fff', bg: '#e67e22' },
  P3: { label: 'P3', color: '#fff', bg: '#f1c40f' },
  P4: { label: 'P4', color: '#fff', bg: '#3498db' },
  P5: { label: 'P5', color: '#fff', bg: '#95a5a6' },
};

const timeAgo = (dateStr: string): string => {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
};

const AlertRow = ({ alert }: { alert: ActiveAlert }) => {
  const theme = useTheme();
  const config = priorityConfig[alert.priority] ?? priorityConfig.P5;

  return (
    <Link
      href={`https://meltwater.app.opsgenie.com/alert/detail/${alert.id}`}
      target="_blank"
      rel="noreferrer"
      underline="none"
      color="inherit"
      sx={{
        display: 'block',
        px: 2.5,
        py: 1.5,
        transition: 'background-color 150ms ease',
        '&:hover': {
          backgroundColor: getSurfaceColor(theme, 'subtle'),
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
        <Chip
          label={config.label}
          size="small"
          sx={{
            backgroundColor: config.bg,
            borderRadius: '4px',
            color: config.color,
            fontSize: '0.65rem',
            fontWeight: 700,
            height: 20,
            letterSpacing: '0.03em',
          }}
        />
        {alert.acknowledged && (
          <Typography
            variant="caption"
            color="success.main"
            sx={{ fontWeight: 600 }}
          >
            ACK
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {timeAgo(alert.createdAt)}
        </Typography>
      </Box>
      <Typography variant="subtitle2" sx={{ lineHeight: 1.4 }}>
        {alert.message}
      </Typography>
      {alert.owner && (
        <Typography variant="caption" color="text.secondary">
          {alert.owner.replace(/@meltwater\.com$/, '')}
        </Typography>
      )}
    </Link>
  );
};

export const OpsGenie = () => {
  const theme = useTheme();
  const tokens = getThemeTokens(theme);
  const {
    value: schedules,
    loading: loadingSchedules,
    error: onCallError,
    retry: retryOnCall,
  } = useOnCall();
  const {
    value: alerts,
    loading: loadingAlerts,
    error: alertsError,
    retry: retryAlerts,
  } = useActiveAlerts();

  const emails = useMemo(
    () => [
      ...new Set([
        ...(schedules ?? []).flatMap(s => s.recipients),
        ...(schedules ?? [])
          .map(s => s.nextRecipient)
          .filter((e): e is string => Boolean(e)),
      ]),
    ],
    [schedules],
  );
  const { userMap } = useUsersByEmail(emails);

  const loading = loadingSchedules || loadingAlerts;

  return (
    <Paper sx={{ px: 0, py: 3 }}>
      {/* On-call section */}
      <Box sx={{ px: 3 }}>
        {loadingSchedules ? (
          <Stack spacing={1}>
            <Skeleton variant="rounded" height={48} />
          </Stack>
        ) : onCallError && !schedules?.length ? (
          <Box
            sx={{
              backgroundColor: getSurfaceColor(theme, 'subtle'),
              border: `1px solid ${getSurfaceBorder(theme)}`,
              borderRadius: `${radii.md}px`,
              px: 3,
              py: 2,
              textAlign: 'center',
            }}
          >
            <Typography color="text.secondary" variant="body2" sx={{ mb: 1 }}>
              Couldn't load on-call schedules.
            </Typography>
            <Button
              size="small"
              startIcon={<RefreshRoundedIcon />}
              onClick={retryOnCall}
            >
              Retry
            </Button>
          </Box>
        ) : !schedules?.length ? (
          <Box
            sx={{
              backgroundColor: getSurfaceColor(theme, 'subtle'),
              border: `1px solid ${getSurfaceBorder(theme)}`,
              borderRadius: `${radii.md}px`,
              px: 3,
              py: 2,
              textAlign: 'center',
            }}
          >
            <Typography color="text.secondary" variant="body2">
              No on-call schedules found for your teams.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {schedules.map(schedule => {
              const nextOnCall = formatNextOnCall(schedule);
              const isOnCallNow = schedule.myNextOnCall?.start === 'now';

              return (
                <Box
                  key={schedule.scheduleName}
                  sx={{
                    backgroundColor: getSurfaceColor(theme, 'subtle'),
                    border: `1px solid ${getSurfaceBorder(theme)}`,
                    borderRadius: `${radii.md}px`,
                    px: 2.5,
                    py: 2,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: tokens.textMuted,
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {schedule.teamName}
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: schedule.nextRecipient
                        ? '1fr 1fr'
                        : '1fr',
                      columnGap: 2,
                      rowGap: 1,
                      mt: 1.5,
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                    >
                      <PhoneInTalkIcon
                        sx={{ color: 'primary.main', fontSize: 16 }}
                      />
                      <Typography variant="subtitle2">On-call</Typography>
                    </Box>
                    {schedule.nextRecipient && schedule.nextStartAt && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography
                          sx={{
                            color: tokens.textMuted,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                          }}
                        >
                          Next · {formatDate(schedule.nextStartAt)}
                        </Typography>
                      </Box>
                    )}
                    <EntityRefChips
                      refs={schedule.recipients.map(emailToUserRef)}
                      entityMap={userMap}
                      emptyText="No one on-call"
                    />
                    {schedule.nextRecipient && (
                      <EntityRefChips
                        refs={[emailToUserRef(schedule.nextRecipient)]}
                        entityMap={userMap}
                        emptyText=""
                      />
                    )}
                  </Box>
                  {nextOnCall && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        mt: 1,
                      }}
                    >
                      <EventIcon
                        sx={{
                          fontSize: 14,
                          color: isOnCallNow
                            ? 'warning.main'
                            : tokens.textMuted,
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: isOnCallNow
                            ? 'warning.main'
                            : tokens.textMuted,
                          fontWeight: isOnCallNow ? 700 : 600,
                        }}
                      >
                        {nextOnCall}
                      </Typography>
                    </Box>
                  )}
                  <Link
                    href={`https://meltwater.app.opsgenie.com/settings/schedule/detail/${schedule.scheduleId}`}
                    target="_blank"
                    rel="noreferrer"
                    underline="hover"
                    variant="caption"
                    sx={{ fontWeight: 600, mt: 1, display: 'inline-block' }}
                  >
                    View schedule →
                  </Link>
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>

      {/* Divider */}
      <Divider sx={{ my: 2.5 }} />

      {/* Alerts section */}
      <Box sx={{ px: 3, display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <WarningAmberIcon
          sx={{
            color: alerts?.length ? 'warning.main' : 'text.disabled',
            fontSize: 20,
          }}
        />
        <Typography variant="h6">Alerts</Typography>
        {!loading && alerts && alerts.length > 0 && (
          <Typography
            variant="caption"
            sx={{
              color: tokens.textMuted,
              fontWeight: 600,
            }}
          >
            {alerts.length}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Link
          href="https://meltwater.app.opsgenie.com/alert"
          target="_blank"
          rel="noreferrer"
          underline="hover"
          variant="caption"
          sx={{ fontWeight: 600 }}
        >
          All alerts →
        </Link>
      </Box>

      {loadingAlerts ? (
        <Stack spacing={1} sx={{ px: 3 }}>
          <Skeleton variant="rounded" height={56} />
        </Stack>
      ) : alertsError && !alerts?.length ? (
        <Box sx={{ px: 3 }}>
          <Box
            sx={{
              backgroundColor: getSurfaceColor(theme, 'subtle'),
              border: `1px solid ${getSurfaceBorder(theme)}`,
              borderRadius: `${radii.md}px`,
              px: 3,
              py: 2,
              textAlign: 'center',
            }}
          >
            <Typography color="text.secondary" variant="body2" sx={{ mb: 1 }}>
              Couldn't load alerts.
            </Typography>
            <Button
              size="small"
              startIcon={<RefreshRoundedIcon />}
              onClick={retryAlerts}
            >
              Retry
            </Button>
          </Box>
        </Box>
      ) : !alerts?.length ? (
        <Box sx={{ px: 3 }}>
          <Box
            sx={{
              backgroundColor: getSurfaceColor(theme, 'subtle'),
              border: `1px solid ${getSurfaceBorder(theme)}`,
              borderRadius: `${radii.md}px`,
              px: 3,
              py: 2,
              textAlign: 'center',
            }}
          >
            <Typography color="text.secondary" variant="body2">
              No open alerts for your teams.
            </Typography>
          </Box>
        </Box>
      ) : (
        <Stack
          divider={
            <Box sx={{ px: 2.5 }}>
              <Box sx={{ borderTop: `1px solid ${getSurfaceBorder(theme)}` }} />
            </Box>
          }
          spacing={0}
        >
          {alerts.map(alert => (
            <AlertRow key={alert.id} alert={alert} />
          ))}
        </Stack>
      )}
    </Paper>
  );
};
